<template>
  <div class="tc-select">
    <v-autocomplete
      id="amount-fixed-input"
      v-model.lazy="selectedUtid"
      :items="itemOptions"
      :item-value="itemUtid"
      :item-text="selectedOptionText"
      :attach="attachTo"
      name="amount-fixed-input"
      solo
      hide-details
      type="button"
      :aria-label="$t('catalog-aria-label-amount-fixed-input')"
      @input="setNewItemValue"
    >
      <template
        slot="item"
        slot-scope="{ item }"
      >
        <div class="item-template">
          <span>{{ selectedOptionText(item) }}</span>
          <span v-if="item.disabled">
            - {{ $t('reward-fixed-out-of-stock') }}
          </span>
        </div>
      </template>
    </v-autocomplete>
    <input
      id="amount-fixed-input-validator"
      v-model.lazy="selectedItemValue"
      v-validate="validationRules"
      type="hidden"
      name="amount-fixed-input-validator"
    >

    <div class="error-block mt-1">
      <validation-tip
        :error-bag="errors"
        rule="required"
        control="amount-fixed-input-validator"
        i18n-key="reward-variable-valid-amount"
      />
      <validation-tip
        :error-bag="errors"
        rule="remainingBalance"
        control="amount-fixed-input-validator"
        i18n-key="reward-variable-valid-amount-balance"
      />
      <validation-tip
        :error-bag="errors"
        rule="feeCheck"
        control="amount-fixed-input-validator"
        i18n-key="reward-variable-valid-amount-balance-with-fee"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { GET_REWARD_LINK_BALANCE } from '@/store/get-types'
import ValidationTip from '../../shared/forms/ValidationTip.vue'
import CartItem from '@/store/classes/cart-item'

export default {
  name: 'AmountFixedInput',
  components: {
    ValidationTip
  },
  inject: ['$validator'],
  props: {
    selectedItem: {
      type: Object,
      required: true
    },
    itemOptions: {
      type: Array,
      required: true
    },
    value: {
      type: [String],
      required: false,
      default: undefined
    },
    cartItem: {
      type: [CartItem, Object],
      required: false,
      default: undefined
    },
    attachTo: {
      type: String,
      required: false,
      default: undefined
    }
  },
  data () {
    return {
      selectedUtid: null,
      cachedItem: {}
    }
  },
  computed: {
    ...mapGetters({
      availableBalance: GET_REWARD_LINK_BALANCE
    }),
    validationRules () {
      const { cachedItem, availableBalance } = this
      return {
        required: true,
        remainingBalance: [availableBalance, cachedItem],
        feeCheck: [availableBalance, cachedItem]
      }
    },
    selectedItemValue () {
      if (!this.selectedUtid) {
        return 0
      }
      const { faceValue = 0 } =
        this.itemOptions.find(option => option.utid === this.selectedUtid) || {}
      return faceValue
    }
  },
  watch: {
    value () {
      this.setDefaultSelectedUtid()
    },
    item: function (oldItem, newItem) {
      if (oldItem.utid !== newItem.utid) {
        this.cachedItem = JSON.parse(JSON.stringify(newItem))
      }
    }
  },
  async mounted () {
    this.cachedItem = JSON.parse(JSON.stringify(this.selectedItem))
    await this.$nextTick()
    this.getCartItem()
  },
  methods: {
    itemUtid: item => item.utid,
    setNewItemValue (val) {
      this.$emit('input', `${val}`)
    },
    selectedOptionText (item) {
      // item passed in
      if (item.faceValue) {
        return `${item.faceValue} ${item.currencyCode}`
      } else if (item.label && this.cartItem) {
        // passed in via cartItem
        return `${item.label} ${this.cartItem.currencyCode}`
      } else if (this.cachedItem) {
        // not passed in but taken from the item
        return `${this.cachedItem.faceValue} ${this.cachedItem.currencyCode}`
      }
    },
    setDefaultSelectedUtid () {
      if (
        !this.itemOptions
          .map(({ utid }) => utid)
          .find(utid => utid === this.value)
      ) {
        // if the value (utid) does not exist in itemOptions, just select the 1st one.
        const { utid } = this.itemOptions[0]
        this.selectedUtid = utid
      } else {
        // else set VSelect model to utid provided via props
        this.selectedUtid = this.value
      }
    },
    getCartItem () {
      if (this.cartItem) {
        this.selectedUtid = this.cartItem.utid
      } else if (this.value) {
        this.setDefaultSelectedUtid()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables';
.tc-select::v-deep {
  .v-text-field > .v-input__control > .v-input__slot {
    box-shadow: none !important;
    border: solid 2px var(--v-primary-base);
  }
}
.item-template {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}
</style>
